import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { RefreshCw, RotateCcw } from 'lucide-react';
import {
    getClaim, getClaimHistory, listManufacturers, newIdempotencyKey, reevaluateClaim, toApiError,
} from '../../services/pocApi';
import useApiResource from '../../components/poc/useApiResource';
import { usePocAccess } from '../../components/poc/PocAccess';
import { DecisionSummary, HistoryTimeline } from '../../components/poc/DecisionDetails';
import {
    ConfirmDialog, DetailList, Disclaimer, EmptyState, ErrorNotice, LoadingState, PageHeader, Panel, POC_LINKS,
    StatusBadge, SubNav,
} from '../../components/poc/PocUi';
import {
    formatAmount, formatNumber, formatTimestamp, manufacturerName, modelLabel, statusLabel,
} from '../../components/poc/pocFormat';

const CLAIM_ID = /^POC-[0-9]{6,}$/;

function DecisionChange({ events }) {
    const ordered = [...(events || [])].sort((a, b) => a.sequence_no - b.sequence_no);
    if (ordered.length < 2) return null;
    const current = ordered[ordered.length - 1];
    const previous = ordered[ordered.length - 2];
    if (current.final_decision === previous.final_decision) return null;
    return (
        <div role="note" className="border border-primary bg-surface p-4 space-y-3" aria-label="Decision change">
            <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="flex items-center gap-2">Previous Decision: <StatusBadge status={previous.final_decision} /></span>
                <span aria-hidden="true">→</span>
                <span className="flex items-center gap-2">Current Decision: <StatusBadge status={current.final_decision} /></span>
            </div>
            <p className="text-xs text-text-muted">
                Decision #{current.sequence_no} was produced by re-evaluating the claim against the policies active at that time
                ({current.policy_id || 'no policy selected'}; previously {previous.policy_id || 'no policy selected'}).
                Earlier decisions are kept unchanged in the history below.
            </p>
        </div>
    );
}

function Reevaluate({ claimId, onDone }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    // One key per confirmed re-evaluation, reused if that request has to be retried.
    const key = useRef(null);
    const inFlight = useRef(false);

    const start = () => {
        key.current = key.current || newIdempotencyKey();
        setError(null);
        setOpen(true);
    };
    const confirm = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        try {
            const claim = await reevaluateClaim(claimId, key.current);
            key.current = null;
            setOpen(false);
            setMessage(claim.replayed
                ? `This re-evaluation had already been recorded. Current decision: ${statusLabel(claim.claim_status)}.`
                : `Re-evaluation recorded. Current decision: ${statusLabel(claim.claim_status)}.`);
            onDone();
        } catch (err) {
            const apiError = toApiError(err);
            if (apiError.kind === 'conflict') key.current = null;
            setError(apiError);
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    return (
        <>
            <button type="button" className="btn btn-primary gap-2" onClick={start}>
                <RotateCcw className="w-4 h-4" aria-hidden="true" />Reevaluate Claim
            </button>
            {message && <p role="status" className="basis-full text-sm border-l-2 border-[#1F7A3F] pl-3">{message}</p>}
            <ConfirmDialog open={open} title={`Reevaluate ${claimId}?`} confirmLabel="Reevaluate" busy={busy}
                           onConfirm={confirm} onCancel={() => setOpen(false)}>
                <ul className="list-disc pl-5 space-y-1">
                    <li>The claim is evaluated again against the warranty policies and authorization rules that are active now.</li>
                    <li>A new decision-history event is added.</li>
                    <li>The claim's current status may change.</li>
                    <li>Previous history entries remain unchanged.</li>
                </ul>
                <ErrorNotice error={error} title="Re-evaluation failed" />
            </ConfirmDialog>
        </>
    );
}

export default function PocClaimDetails() {
    const { claimId } = useParams();
    const location = useLocation();
    const { isAdmin } = usePocAccess();
    const validId = CLAIM_ID.test(claimId || '');
    const [version, setVersion] = useState(0);
    const claim = useApiResource((signal) => (validId ? getClaim(claimId, signal) : Promise.resolve(null)), [claimId, version]);
    const history = useApiResource((signal) => (validId ? getClaimHistory(claimId, signal) : Promise.resolve(null)), [claimId, version]);
    const manufacturers = useApiResource((signal) => listManufacturers(signal), []);
    const historyRef = useRef(null);
    const refresh = () => setVersion((v) => v + 1);

    useEffect(() => {
        if (location.hash === '#history' && history.data) historyRef.current?.scrollIntoView?.({ block: 'start' });
    }, [location.hash, history.data]);

    if (!validId) {
        return (
            <div className="space-y-8 max-w-6xl mx-auto">
                <SubNav label="POC claim management" links={POC_LINKS} />
                <EmptyState>"{claimId}" is not a valid POC claim ID. <Link className="underline" to="/poc-claims/history">Back to the claim list</Link></EmptyState>
            </div>
        );
    }

    const c = claim.data;
    const names = manufacturers.data || [];
    return (
        <div className="space-y-8 max-w-6xl mx-auto">
            <SubNav label="POC claim management" links={POC_LINKS} />
            <PageHeader
                title={`Claim ${claimId}`}
                subtitle="Claim facts as submitted, the current decision and every stored decision event."
                actions={<>
                    <button type="button" className="btn btn-secondary gap-2" onClick={refresh} disabled={claim.loading || history.loading}>
                        <RefreshCw className="w-4 h-4" aria-hidden="true" />Refresh
                    </button>
                    {isAdmin && c && <Reevaluate claimId={claimId} onDone={refresh} />}
                </>}
            />
            <Disclaimer />

            {claim.loading && !c && <LoadingState label="Loading claim…" />}
            {claim.error?.kind === 'not_found'
                ? <EmptyState>Claim {claimId} was not found. <Link className="underline" to="/poc-claims/history">Back to the claim list</Link></EmptyState>
                : <ErrorNotice error={claim.error} onRetry={refresh} />}

            {c && (
                <>
                    <Panel title="Claim Details">
                        <DetailList items={[
                            ['Claim ID', c.claim_id],
                            ['Current decision', <StatusBadge status={c.claim_status} />],
                            ['Manufacturer', `${manufacturerName(names, c.manufacturer_code)}${c.manufacturer_code ? ` (${c.manufacturer_code})` : ''}`],
                            ['Model', modelLabel(c, 'Manufacturer level')],
                            ['Model year', c.model_year ?? 'Not provided'],
                            ['VIN', c.vin],
                            ['Warranty start date', c.warranty_start_date || 'Not provided'],
                            ['Repair date', c.repair_date || 'Not provided'],
                            ['Odometer', c.odometer_reading === null ? 'Not provided' : `${formatNumber(c.odometer_reading)} ${c.distance_unit || ''}`],
                            ['Coverage type', c.coverage_type],
                            ['Component category', c.component_category || 'Not provided'],
                            ['Part codes', c.part_codes?.length ? c.part_codes.join(', ') : 'None'],
                            ['Sublet codes', c.sublet_codes?.length ? c.sublet_codes.join(', ') : 'None'],
                            ['Claim amount', c.claim_amount ? formatAmount(c.claim_amount, c.currency) : 'Not provided'],
                            ['Submitted', formatTimestamp(c.created_at)],
                            ['Last updated', formatTimestamp(c.updated_at)],
                        ]} />
                    </Panel>
                    <Panel title="Current Decision">
                        <DecisionSummary decision={c.latest_decision} isAdmin={isAdmin} />
                    </Panel>
                </>
            )}

            <div ref={historyRef} id="history" className="scroll-mt-8">
                <Panel title="Decision History">
                    {history.loading && !history.data && <LoadingState label="Loading decision history…" />}
                    {/* A claim-level error is already shown above; do not repeat it here. */}
                    {!claim.error && <ErrorNotice error={history.error} onRetry={refresh} />}
                    {claim.error && <EmptyState>Decision history is not available.</EmptyState>}
                    {history.data && (
                        <div className="space-y-6">
                            <DecisionChange events={history.data.events} />
                            <HistoryTimeline events={history.data.events} isAdmin={isAdmin} />
                        </div>
                    )}
                </Panel>
            </div>
        </div>
    );
}
